import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/hooks/use-toast';
import { createSubdomain, updateSubdomain } from '@/services/api/chatbot';
import { getDomains } from '@/services/api/domains';
import type { ChatbotSubdomain } from '@/types/chatbot';
import { Info } from 'lucide-react';
import DnsInstructions from './DnsInstructions';

const PROXY_HOST = import.meta.env.VITE_CHATBOT_PROXY_HOST || 'chatbot.rocketgroup.io';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    editing: ChatbotSubdomain | null;
}

export default function SubdomainFormModal({ open, onOpenChange, editing }: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const isEdit = !!editing;

    const [parentId, setParentId] = useState<string>('');
    const [subdomain, setSubdomain] = useState('');
    const [name, setName] = useState('');
    const [lang, setLang] = useState('pt');

    // apos criar, mostra modal de DNS
    const [createdSub, setCreatedSub] = useState<ChatbotSubdomain | null>(null);

    useEffect(() => {
        if (open) {
            setParentId(editing ? String(editing.parent_domain_id) : '');
            setSubdomain(editing?.subdomain || '');
            setName(editing?.name || '');
            setLang(editing?.default_language || 'pt');
            setCreatedSub(null);
        }
    }, [open, editing]);

    const { data: domains } = useQuery({
        queryKey: ['domains-for-chatbot'],
        queryFn: getDomains,
        enabled: open,
    });

    const createMut = useMutation({
        mutationFn: createSubdomain,
        onSuccess: (sub) => {
            toast({ title: 'Subdominio criado!', description: 'Configure o DNS pra ativar o SSL.' });
            queryClient.invalidateQueries({ queryKey: ['chatbot-subdomains'] });
            // nao fecha o modal - troca pro view de instrucoes DNS
            setCreatedSub(sub);
        },
        onError: (e: any) => toast({
            variant: 'destructive', title: 'Erro ao criar',
            description: e.response?.data?.detail || e.message,
        }),
    });

    const updateMut = useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) => updateSubdomain(id, data),
        onSuccess: () => {
            toast({ title: 'Subdominio atualizado!' });
            queryClient.invalidateQueries({ queryKey: ['chatbot-subdomains'] });
            onOpenChange(false);
        },
        onError: (e: any) => toast({
            variant: 'destructive', title: 'Erro ao atualizar',
            description: e.response?.data?.detail || e.message,
        }),
    });

    const handleSave = () => {
        if (!parentId) { toast({ variant: 'destructive', title: 'Selecione o dominio pai' }); return; }
        if (!subdomain) { toast({ variant: 'destructive', title: 'Informe o subdominio' }); return; }

        if (isEdit && editing) {
            updateMut.mutate({ id: editing.id, data: { name, default_language: lang } });
        } else {
            createMut.mutate({
                parent_domain_id: Number(parentId),
                subdomain: subdomain.trim().toLowerCase(),
                name: name || undefined,
                default_language: lang,
            });
        }
    };

    const domainOptions = (domains || []).map(d => ({
        value: String(d.id),
        label: `${d.domain} (id=${d.id})`,
    }));

    const langOptions = [
        { value: 'pt', label: 'Portugues' },
        { value: 'en', label: 'English' },
        { value: 'es', label: 'Espanol' },
        { value: 'fr', label: 'Francais' },
        { value: 'de', label: 'Deutsch' },
        { value: 'it', label: 'Italiano' },
    ];

    // ============= VIEW: Pos-criacao (instrucoes DNS) =============
    if (createdSub) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Subdominio criado! Proximos passos</DialogTitle>
                        <DialogDescription>
                            Subdominio <span className="font-mono">{createdSub.subdomain}</span> cadastrado.
                            Pra ativar o chatbot, configure o DNS abaixo.
                        </DialogDescription>
                    </DialogHeader>

                    <DnsInstructions subdomain={createdSub} variant="inline" />

                    <DialogFooter>
                        <Button onClick={() => onOpenChange(false)}>
                            Fechar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    // ============= VIEW: Form de criacao/edicao =============
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Editar subdominio' : 'Novo subdominio de chatbot'}</DialogTitle>
                    <DialogDescription>
                        Cada subdominio aponta para um dominio pai e herda os ad_units dele.
                    </DialogDescription>
                </DialogHeader>

                {!isEdit && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900 flex gap-2">
                        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <div>
                            <strong>Antes de criar:</strong> certifique-se de que voce (ou o cliente) consegue alterar o DNS do dominio.
                            Apos cadastrar, voce vai precisar apontar um CNAME pra <span className="font-mono">{PROXY_HOST}</span>.
                            O SSL e emitido automaticamente na 1a request - sem instalacao manual.
                        </div>
                    </div>
                )}

                <div className="space-y-4 py-2">
                    <div>
                        <Label>Dominio pai *</Label>
                        <SearchableSelect
                            value={parentId}
                            onValueChange={setParentId}
                            options={domainOptions}
                            placeholder="Selecione o dominio pai"
                        />
                        <p className="text-[10px] text-slate-500 mt-1">
                            O chatbot herda os ad_units (GAM) desse dominio.
                        </p>
                    </div>

                    <div>
                        <Label>Subdominio *</Label>
                        <Input
                            value={subdomain}
                            onChange={(e) => setSubdomain(e.target.value)}
                            placeholder="chat.dominio.com"
                            disabled={isEdit}
                        />
                        {isEdit ? (
                            <p className="text-[10px] text-slate-500 mt-1">
                                O host nao pode ser alterado depois de criado.
                            </p>
                        ) : (
                            <p className="text-[10px] text-slate-500 mt-1">
                                Sera o endereco onde os usuarios acessam os chats. Ex: quiz.dominio.com
                            </p>
                        )}
                    </div>

                    <div>
                        <Label>Nome (opcional)</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)}
                            placeholder="Ex: Chatbot de Emprestimos" />
                    </div>

                    <div>
                        <Label>Idioma padrao</Label>
                        <SearchableSelect
                            value={lang}
                            onValueChange={setLang}
                            options={langOptions}
                            placeholder="Idioma"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
                    <Button onClick={handleSave}
                        disabled={createMut.isPending || updateMut.isPending}>
                        {isEdit ? 'Salvar' : 'Criar e ver instrucoes de DNS'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
